import type { OnlineOrderDetail, OnlineOrderStatus } from '@erp/shared';
import { ArrowLeft, CheckCircle2, FileText, Pencil, Truck, XCircle } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';

import { Badge, Card, Skeleton } from '../../components/shadcn/index.js';
import { Button, confirmDialog, EmptyState } from '../../components/ui/index.js';
import { usePermission } from '../../hooks/usePermission.js';
import { api } from '../../lib/api.js';
import { useAuthStore } from '../../lib/authStore.js';
import { createDeliveryFromOrder } from '../../lib/delivery.js';
import { moduleEnabled } from '../../lib/modules.js';
import { useEnabledModules } from '../../lib/modulesStore.js';
import { formatMoney, trimMoney } from '../../lib/money.js';

const TONE: Record<OnlineOrderStatus, 'info' | 'success' | 'danger'> = {
  submitted: 'info',
  fulfilled: 'success',
  cancelled: 'danger',
};

export function CmsOrderDetailPage(): JSX.Element {
  const { t, i18n } = useTranslation('cms');
  const { t: tc } = useTranslation();
  const { t: tdel } = useTranslation('delivery');
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const canWrite = usePermission('cms.write');
  const canDeliver = usePermission('delivery.write');
  // The edit form writes a SALES ORDER, so it is that permission that decides —
  // showing the button to someone who only holds cms.write would walk them into
  // a 403 at save time.
  const canEditOrder = usePermission('sales_orders.write');
  const tenant = useAuthStore((s) => s.tenant);
  const deliveryOn = moduleEnabled(useEnabledModules(tenant?.id ?? null), 'delivery');
  const [order, setOrder] = useState<OnlineOrderDetail | null>(null);
  const [status, setStatus] = useState<'loading' | 'ok' | 'notfound'>('loading');
  const [saving, setSaving] = useState(false);
  const [creatingDelivery, setCreatingDelivery] = useState(false);

  const createDelivery = async (): Promise<void> => {
    setCreatingDelivery(true);
    try {
      const dlv = await createDeliveryFromOrder(id);
      toast.success(tdel('actions.created'));
      navigate(`/delivery/${dlv.id}`);
    } catch {
      toast.error(tc('errors:server'));
    } finally {
      setCreatingDelivery(false);
    }
  };

  useEffect(() => {
    setStatus('loading');
    void api().get<OnlineOrderDetail>(`/cms/orders/${id}`)
      .then((r) => { setOrder(r.data); setStatus('ok'); })
      .catch(() => setStatus('notfound'));
  }, [id]);

  const money = (v: string): string => formatMoney(v, { locale: i18n.language, currency: order?.currencyCode ?? 'USD' });

  const changeStatus = async (next: 'fulfilled' | 'cancelled'): Promise<void> => {
    if (next === 'cancelled') {
      const ok = await confirmDialog({ title: t('orders.cancelTitle'), message: t('orders.confirmCancel'), confirmLabel: tc('actions.confirm'), tone: 'danger' });
      if (!ok) return;
    }
    setSaving(true);
    try {
      const r = await api().put<OnlineOrderDetail>(`/cms/orders/${id}/status`, { status: next });
      setOrder(r.data);
      // «منجز» bills the order: say which invoice it became (and if it still
      // needs posting by hand — stock or an approval held it back).
      if (next === 'fulfilled' && r.data.invoice) {
        toast.success(t('orders.invoiced', { defaultValue: 'Fulfilled — invoice {{number}} created.', number: r.data.invoice.docNumber }));
        if (r.data.invoice.status === 'draft') {
          toast.warning(t('orders.invoiceDraft', { defaultValue: 'The invoice is still a draft — open it and post it.' }));
        }
      } else {
        toast.success(t('orders.statusChanged'));
      }
    } catch {
      toast.error(tc('errors:server'));
    } finally {
      setSaving(false);
    }
  };

  if (status === 'loading') return <Card className="p-6"><Skeleton className="h-72 w-full" /></Card>;
  if (status === 'notfound' || !order) return <EmptyState title={t('orders.notFound')} />;

  const addr = order.shippingAddress;
  // The delivery charge is an item-less line (see storefront checkout's
  // deliveryFeeLine) — surface it as a fee in the summary, never as a product row.
  const itemLines = order.lines.filter((l) => l.itemId);
  const deliveryLine = order.lines.find((l) => !l.itemId) ?? null;
  const deliveryFee = deliveryLine ? Number(deliveryLine.lineTotal) : 0;
  const productsSubtotal = Number(order.subtotal) - deliveryFee;

  return (
    <div className="flex flex-col gap-4">
      <Link to="/cms/orders" className="inline-flex w-fit items-center gap-1 text-sm text-fg-muted hover:text-fg">
        <ArrowLeft className="h-4 w-4" aria-hidden />{t('orders.back')}
      </Link>

      <header className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <h1 className="font-mono text-2xl font-semibold">{order.orderNumber}</h1>
          <Badge variant={TONE[order.status]}>{t(`orders.statusVal.${order.status}`, { defaultValue: order.status })}</Badge>
        </div>
        <div className="flex flex-wrap gap-2">
          {canDeliver && deliveryOn && order.status !== 'cancelled' && (
            <Button size="sm" variant="secondary" loading={creatingDelivery} onClick={() => void createDelivery()}>
              <Truck className="h-4 w-4" aria-hidden />{tdel('actions.createDelivery')}
            </Button>
          )}
          {/* Only while the order is still open: once it is fulfilled or
              cancelled it is history, and the server refuses the write too. */}
          {canEditOrder && order.status === 'submitted' && (
            <Link to={`/cms/orders/${id}/edit`}>
              <Button size="sm" variant="secondary">
                <Pencil className="h-4 w-4" aria-hidden />{tc('actions.edit')}
              </Button>
            </Link>
          )}
          {order.invoice && (
            <Link to={`/sales/invoices/${order.invoice.id}`}>
              <Button size="sm" variant="secondary">
                <FileText className="h-4 w-4" aria-hidden />
                {t('orders.openInvoice', { defaultValue: 'Invoice {{number}}', number: order.invoice.docNumber })}
              </Button>
            </Link>
          )}
          {canWrite && order.status === 'submitted' && (
            <>
              <Button size="sm" loading={saving} onClick={() => void changeStatus('fulfilled')}>
                <CheckCircle2 className="h-4 w-4" aria-hidden />{t('orders.markFulfilled')}
              </Button>
              <Button variant="danger" size="sm" disabled={saving} onClick={() => void changeStatus('cancelled')}>
                <XCircle className="h-4 w-4" aria-hidden />{t('orders.cancel')}
              </Button>
            </>
          )}
        </div>
      </header>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <Card className="flex flex-col gap-1 p-4 text-sm">
          <h2 className="mb-1 text-xs font-semibold uppercase tracking-wide text-fg-muted">{t('orders.customer')}</h2>
          <div className="font-medium">{order.customerName}</div>
          {order.customerPhone && <div className="text-fg-muted" dir="ltr">{order.customerPhone}</div>}
        </Card>

        <Card className="flex flex-col gap-1 p-4 text-sm">
          <h2 className="mb-1 text-xs font-semibold uppercase tracking-wide text-fg-muted">{t('orders.shipping')}</h2>
          {addr ? (
            <div className="text-fg">
              <div>{addr.line1}</div>
              {addr.line2 && <div>{addr.line2}</div>}
              <div>{[addr.city, addr.region, addr.postalCode].filter(Boolean).join(', ')}</div>
              <div>{addr.country}</div>
            </div>
          ) : <div className="text-fg-muted">{t('orders.noShipping')}</div>}
        </Card>

        <Card className="flex flex-col gap-1 p-4 text-sm">
          <h2 className="mb-1 text-xs font-semibold uppercase tracking-wide text-fg-muted">{t('orders.payment')}</h2>
          <div className="flex items-center gap-2">
            <span>{order.paymentMethod ? t(`orders.payMethod.${order.paymentMethod}`, { defaultValue: order.paymentMethod }) : '—'}</span>
            {order.fibPaid && <Badge variant="success">{t('orders.fibPaid', { defaultValue: 'Paid · FIB' })}</Badge>}
          </div>
          <div className="text-fg-muted">{order.date}</div>
        </Card>
      </div>

      <Card className="overflow-hidden p-0">
        <table className="w-full text-sm">
          <thead className="bg-bg-subtle text-fg-muted">
            <tr>
              <th className="px-4 py-2 text-start font-medium">{t('orders.item')}</th>
              <th className="px-4 py-2 text-end font-medium">{t('orders.qty')}</th>
              <th className="px-4 py-2 text-end font-medium">{t('orders.price')}</th>
              <th className="px-4 py-2 text-end font-medium">{t('orders.lineTotal')}</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {itemLines.map((l, i) => (
              <tr key={i}>
                <td className="px-4 py-2">{l.description}</td>
                <td className="px-4 py-2 text-end font-mono tabular-nums">{trimMoney(l.quantity)}</td>
                <td className="px-4 py-2 text-end font-mono tabular-nums">{money(l.unitPrice)}</td>
                <td className="px-4 py-2 text-end font-mono tabular-nums">{money(l.lineTotal)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      <div className="flex flex-col gap-3 sm:flex-row sm:justify-between">
        {order.notes && (
          <Card className="flex-1 p-4 text-sm">
            <h2 className="mb-1 text-xs font-semibold uppercase tracking-wide text-fg-muted">{t('orders.notes')}</h2>
            <p className="whitespace-pre-wrap text-fg-muted">{order.notes}</p>
          </Card>
        )}
        <Card className="ms-auto flex w-full max-w-xs flex-col gap-1.5 p-4 text-sm">
          <div className="flex justify-between"><span className="text-fg-muted">{t('orders.subtotal')}</span><span className="font-mono tabular-nums">{money(String(productsSubtotal))}</span></div>
          {deliveryLine && <div className="flex justify-between"><span className="text-fg-muted">{t('orders.delivery', { defaultValue: 'Delivery' })}</span><span className="font-mono tabular-nums">{money(deliveryLine.lineTotal)}</span></div>}
          {Number(order.discountTotal) > 0 && <div className="flex justify-between text-success"><span>{t('orders.discount')}</span><span className="font-mono tabular-nums">−{money(order.discountTotal)}</span></div>}
          {Number(order.taxTotal) > 0 && <div className="flex justify-between"><span className="text-fg-muted">{t('orders.tax')}</span><span className="font-mono tabular-nums">{money(order.taxTotal)}</span></div>}
          <div className="flex justify-between border-t border-border pt-1.5 text-base font-bold"><span>{t('orders.total')}</span><span className="font-mono tabular-nums">{money(order.total)}</span></div>
          {Number(order.cashbackRedeemed ?? 0) > 0 && (
            <>
              <div className="flex justify-between text-success"><span>{t('orders.cashbackUsed', { defaultValue: 'Paid from cashback' })}</span><span className="font-mono tabular-nums">−{money(order.cashbackRedeemed ?? '0')}</span></div>
              <div className="flex justify-between text-base font-bold"><span>{t('orders.amountDue', { defaultValue: 'Amount due' })}</span><span className="font-mono tabular-nums">{money(String(Number(order.total) - Number(order.cashbackRedeemed ?? 0)))}</span></div>
            </>
          )}
        </Card>
      </div>
    </div>
  );
}
